/**
 * Progress indicator component for backup/restore operations.
 * Shows status, progress bar, and messages.
 */

import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Progress } from '../../../components/ui/progress';
import type { BackupTaskStatus } from '../types/backupRestore';
import { cn } from '../../../lib/utils';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type ProgressIndicatorProps = {
  status: BackupTaskStatus;
  progress?: number;
  message?: string;
  error?: string;
  className?: string;
  'data-testid'?: string;
};

function getStatusIcon(status: BackupTaskStatus) {
  switch (status) {
    case 'succeeded':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'failed':
      return <XCircle className="h-5 w-5 text-destructive" />;
    case 'cancelled':
      return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
    case 'inProgress':
    case 'created':
    default:
      return <RandomDotmSpinner size={20} className="text-primary" />;
  }
}

function getStatusLabel(status: BackupTaskStatus): string {
  switch (status) {
    case 'created':
      return 'Starting...';
    case 'inProgress':
      return 'In Progress';
    case 'succeeded':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
  }
}

export function ProgressIndicator({
  status,
  progress,
  message,
  error,
  className,
  'data-testid': dataTestId,
}: ProgressIndicatorProps) {
  const isInProgress = status === 'created' || status === 'inProgress';

  return (
    <div className={cn('space-y-3', className)} data-testid={dataTestId}>
      {/* Status header */}
      <div className="flex items-center gap-2">
        {getStatusIcon(status)}
        <span className="font-medium">{getStatusLabel(status)}</span>
        {progress !== undefined && isInProgress && (
          <span className="text-sm text-muted-foreground ml-auto tabular-nums">
            {Math.round(progress)}%
          </span>
        )}
      </div>

      {/* Progress bar (only show during active operations) */}
      {isInProgress && (
        <Progress
          value={progress ?? 0}
          className="h-2"
          data-testid="export-progress-bar"
        />
      )}

      {/* Status message */}
      {message && status !== 'failed' && (
        <p className="text-sm text-muted-foreground" data-testid="export-progress-message">
          {message}
        </p>
      )}

      {/* Error message */}
      {(error || (status === 'failed' && message)) && (
        <p className="text-sm text-destructive" data-testid="export-error-message">
          {error || message}
        </p>
      )}
    </div>
  );
}
